import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../Styles";
import { useMemo, useState } from "react";
import { NewButton, PrimaryPopover } from "../BaseComponents";
import { ButtonTypes } from "../../Definitions";
import { Section, Station, StationTerminal } from "../../Definitions/newSectionModalInterfaces";
import { parseGridArea } from "../LocationFeeder/Modals/newLfSectionModalLvl3";
import { getBorderRadius } from "../_Components";

interface ModalLayoutLvl3_props {
  section: Section
  rows: number;
  columns: number;
  locations: Station[];
  deviceInfo: {
    id: number;
    name: string;
    systemId: number | undefined;
    type: number;
  }[]
  additionalFields: {
    key: StationTerminal["__terminalType"];
    label: string;
    condition?: (station: Station) => boolean;
  }[]
}

export default function ModalLayoutLvl3({ rows, columns, locations, section, deviceInfo, additionalFields }: ModalLayoutLvl3_props) {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const [, setIsOpen] = useState<boolean>(false)
  const [isExpanded, setExpand] = useState<boolean>(false)

  // #region | Station View
  const stationView = useMemo(() => {
    const views: JSX.Element[] = [];

    Array.from(locations).forEach((location) => {
      const [row, col] = location.visual ? parseGridArea(location.visual) : [0, 0];
      const borderRadius = !mobileXs ? getBorderRadius({ row, col, columns: section.columns, rows: section.rows }) : undefined;

      const stationElement: JSX.Element[] = [];
      const popOverElement: JSX.Element[] = [];

      const name = location.name ?? "";
      stationElement.push(
        <div key={'name_' + location.stationId} style={{ whiteSpace: "nowrap", gridArea: "1/1/2/2", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
          {`${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })}: `} <b>{name}</b>
        </div>
      );

      const iosUsed = Array.from(new Set(Object.values(location.terminals).map((terminal) => terminal.io)));
      const ioBoards = deviceInfo.filter((device) => iosUsed.includes(device.id)).map((io) => io.name).join(", ");
      stationElement.push(
        <div key={'ioBoard_' + location.stationId} style={{ whiteSpace: "nowrap", gridArea: "1/2/2/3", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
          {`${t("devices.aicoIo", { defaultValue: "I/O" })}: `} <b>{ioBoards}</b>
        </div>
      );
      additionalFields.forEach(({ key, label, condition }) => {
        if (condition && !condition(location)) return;

        if (key !== 'feederOutput' && key !== 'feederInput') {
          let value = location.terminals[key]?.port;
          if (key === 'gateMotorInput') {
            value = location.terminals['gateMotorOutput']?.terminalConfig?.sensor_p;
          }

          if (value == null) return;

          if (key === 'gateMotorInput' || key === 'gateSensor' || key === 'buttonInput') {
            value -= 100;
          }

          if (key === 'readerInput') {
            value -= 200;
          }

          popOverElement.push(
            <p key={`${key}_${location.stationId}`} style={{
              whiteSpace: "nowrap",
              color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary,
              ...fonts.paragraph,
              ...fonts.fontSize4,
            }}>
              {`${label}: ${value}`}
            </p>
          );
          return;
        }
        Array.from({ length: section?.feeder ?? 0 }).forEach((_, feederIndex) => {
          let value = location.terminals[`feederOutput-${feederIndex}`]?.port

          if (key === 'feederInput') {
            value = location.terminals[`feederOutput-${feederIndex}`]?.terminalConfig?.sensor_p!;
          }
          if (value == null) return;

          if (key === 'feederInput') {
            value -= 100;
          }

          popOverElement.push(
            <p key={`${key}_${feederIndex}_${location.stationId}`} style={{
              whiteSpace: "nowrap",
              color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary,
              ...fonts.paragraph,
              ...fonts.fontSize4,
            }}>
              {`${label} ${feederIndex+1}: ${value}`}
            </p>
          )
        });
      });

      views.push(
        <div key={'station_' + location.stationId} style={{
          gridArea: location.visual,
          backgroundColor: colors.greyLightSecondary,
          display: "grid",
          padding: "0px 10px",
          gridTemplateColumns: isExpanded ? "repeat(2, 1fr)" : "1fr 2fr 20px",
          gridTemplateRows: isExpanded ? "30px 1fr" : "30px",
          gap: "0px 10px",
          borderRadius
        }}>
          {stationElement}
          <div style={{ gridArea: isExpanded ? "2/1/3/3" : "1/3/2/4", display: "flex", alignItems: "center", borderTop: isExpanded ? "2px solid " + colors.greyTertiary : "none" }}>
            {isExpanded ? <div style={{ display: "flex", flexDirection: "column", gap: "5px", padding: "10px 0px" }}>{popOverElement}</div>
              : (<PrimaryPopover
                content={
                  <div onClick={() => setIsOpen(true)} style={{ cursor: "pointer", display: "flex" }}>
                    <glyphs.Note fillColor={colors.greyDarkPrimary} />
                  </div>
                }
                overrides={{ popoverBgColor: colors.blackPrimary }}
                popoverHeader={`${t("devices.ioSpecifications", { defaultValue: "i/o specifications" })}`}
                popoverContent={
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {popOverElement}
                  </div>
                }
              />)
            }
          </div>
        </div>
      );
    });

    return views;
  }, [locations, mobileXs, section.columns, section.rows, section?.feeder, t, deviceInfo, additionalFields, isExpanded]);
  // #endregion

  return (
    <div style={{ width: "100%", display: "grid", gridTemplateRows: "20px 2px 1fr 2px", gap: "5px", gridTemplateColumns: "1fr", height: "100%" }}>

      {/* Expand */}
      <div style={{ gridArea: "1/1/2/2", display: "flex", alignItems: "end", justifyContent: "flex-end", padding: "0px 22px", boxSizing: "border-box" }}>
        <NewButton
          content={isExpanded ? t("locationFeederPages.sections.collapse", { defaultValue: "collapse" }) : t("locationFeederPages.sections.expand", { defaultValue: "expand" })}
          buttonType={ButtonTypes.textButton}
          endEnhancer={glyphs.Arrow({ rotate: isExpanded ? 90 : -90, size: "15px", transparency: isExpanded ? 1 : 0.3 })}
          onClick={() => setExpand(!isExpanded)}
        />
      </div>

      {/* Seperator */}
      <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />

      {/* Content */}
      <div style={{
        gridArea: "3/1/4/2",
        display: !mobileXs ? "grid" : "flex",
        flexDirection: "column",
        gridTemplateRows: !mobileXs ? `repeat(${rows}, 1fr)` : "none",
        gridTemplateColumns: !mobileXs ? `repeat(${columns}, 1fr)` : "none",
        gap: "10px",
        borderRadius: "10px",
        overflowX: "hidden",
        overflowY: "auto",
        padding: "10px",
        height: "100%",
        maxHeight: "40vh",
        boxSizing: "border-box",
        scrollbarWidth: "thin",
        scrollbarColor: colors.blueDarkSecondary + colors.transparent
      }}>
        {stationView}
      </div>

      {/* Seperator */}
      <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
    </div>
  )
}
